import { useEffect, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { api } from '../../services/api';
import { PublishModal } from './AssetGallery';
import { CreditCostNotice } from '../credits/CreditCostNotice';
import { usePipelineCostItems, useStepCostItems, ESTIMATE_NOTE } from '../credits/pipelineCost';
import { useCreditConfirm } from '../credits/useCreditConfirm';
import { WizardHeader, WizardFooter } from './ecommerce/shared';
import { ProductSourcePicker, EMPTY_MANUAL, type ManualProduct, type ProductSource } from './ecommerce/ProductSourcePicker';
import { PipelinePicker } from './ecommerce/PipelinePicker';
import { GenerationsGallery } from './ecommerce/GenerationsGallery';
import { VideoPipelineFlow, VIDEO_KIND_META, videoStepChip, type VideoPipeline as Pipeline, type VideoPipelineStep as PipelineStep } from './ecommerce/VideoPipelineFlow';

interface VideoJob {
  id: string; status: string; progress: number; currentStep?: string; productId?: string;
  steps: PipelineStep[]; input?: any;
  output?: {
    // preOverlayUrl / overlayUrl: as duas versões do merge (sem e com overlays).
    // overlaysOn diz qual delas está em `url`. Ausentes em jobs antigos.
    results?: Array<{ stepId: string; title: string; kind: string; assetKind?: string; imageUrl?: string; url?: string; prompt?: string; script?: string; preOverlayUrl?: string | null; overlayUrl?: string | null; overlaysOn?: boolean }>;
    seo?: any[];
    _pendingRegen?: any;
    _regenError?: string;
    // Entrega parcial: o backend reprocessa as etapas que falharam e, quando ainda
    // assim alguma não sai, registra aqui o que faltou (com estorno dos créditos).
    partial?: boolean;
    failures?: Array<{ stepId: string; title: string; kind: string; message: string }>;
    summary?: { requested: number; delivered: number; failed: number };
  };
  error?: string; createdAt?: string;
}

const STATUS_LABEL: Record<string, string> = {
  queued: 'Na fila', running: 'Em execução', awaiting_approval: 'Aguardando aprovação',
  regenerating: 'Regerando', awaiting_adjustment: 'Aguardando aprovação de ajuste',
  completed: 'Concluído', failed: 'Falhou',
};
const IS_VIDEO = (assetKind?: string, kind?: string) => assetKind === 'video' || ['runway', 'avatar_chromakey', 'avatar_fullscreen', 'merge'].includes(kind || '');


// ─── Execução / resultados do job ────────────────────────────────────────────
function JobRunner({ job, products, onChanged }: { job: VideoJob; products: any[]; onChanged: () => void | Promise<void> }) {
  const [busy, setBusy] = useState(false);
  const [retryingStepId, setRetryingStepId] = useState<string | null>(null);
  const retrySubmitting = useRef(false);
  const [actionStepId, setActionStepId] = useState<string | null>(null); // etapa em ação (regerar/salvar/enviar)
  const [selectedStepId, setSelectedStepId] = useState<string | null>(null); // vídeo selecionado p/ ações
  const [publishStepId, setPublishStepId] = useState<string | null>(null); // vídeo no modal de postagem (redes sociais)
  const [modalAction, setModalAction] = useState<{ type: 'save' | 'send'; stepId: string } | null>(null);
  const [modalQuery, setModalQuery] = useState('');
  const [mktTargets, setMktTargets] = useState<any[]>([]);
  const [selectedVideoTargets, setSelectedVideoTargets] = useState<string[]>([]);
  const [targetsLoading, setTargetsLoading] = useState(false);
  const results = (job.output?.results || []).filter(r => r.imageUrl || r.url);
  const videoResults = results.filter(r => IS_VIDEO(r.assetKind, r.kind));
  const mergeResult = results.find((r: any) => r.kind === 'merge') || null;
  const seo = job.output?.seo || [];
  const approvalStep = (job.steps || []).find(s => s.status === 'awaiting_approval');
  const approvalResult = results.find(r => r.stepId === approvalStep?.id);
  // Estimativa (custo de provedor) se o usuário optar por regerar este 1º asset.
  const isRunning = job.status === 'running' || job.status === 'queued' || job.status === 'regenerating';
  const hasProduct = !!job.productId;

  const approve = async () => {
    setBusy(true);
    try { await api.approveEcommerceVideoJob(job.id); toast.success('Aprovado! Gerando o restante...'); onChanged(); }
    catch (e: any) { toast.error(e.message || 'Erro'); } finally { setBusy(false); }
  };
  const regenerateFirst = async () => {
    const feedback = window.prompt('Ajuste desejado (opcional):') || undefined;
    setBusy(true);
    try { await api.regenerateEcommerceVideoFirst(job.id, feedback); toast.success('Regerando...'); onChanged(); }
    catch (e: any) { toast.error(e.message || 'Erro'); } finally { setBusy(false); }
  };
  const [regenStep, setRegenStep] = useState<{ id: string; title: string; isImage: boolean; kind?: string | null } | null>(null);
  const approvalCost = useStepCostItems('video', approvalStep?.kind ? { id: approvalStep.id, kind: approvalStep.kind, title: approvalStep.title } : null);
  const regenCost = useStepCostItems('video', regenStep?.kind ? { id: regenStep.id, kind: regenStep.kind, title: regenStep.title } : null);
  const [regenFeedback, setRegenFeedback] = useState('');
  const [regenRefUrl, setRegenRefUrl] = useState('');
  const [regenRefUploading, setRegenRefUploading] = useState(false);

  const regenerateStep = (stepId: string) => {
    const r = (job.output?.results || []).find(x => x.stepId === stepId);
    // O kind define o que é cobrado ao regerar (cena vs avatar; frame/merge não custam).
    const stepKind = (job.steps || []).find(s => s.id === stepId)?.kind || r?.kind || null;
    setRegenStep({ id: stepId, title: r?.title || 'Asset', isImage: !IS_VIDEO(r?.assetKind, r?.kind), kind: stepKind });
    setRegenFeedback('');
    setRegenRefUrl('');
  };

  const uploadRegenRef = async (file: File) => {
    setRegenRefUploading(true);
    try {
      const fd = new FormData();
      fd.append('photo', file);
      const res = await api.uploadEcommerceVideoReference(fd);
      setRegenRefUrl(res.imageUrl);
    } catch (e: any) { toast.error(e.message || 'Erro ao enviar imagem de referência'); }
    finally { setRegenRefUploading(false); }
  };

  const submitRegen = async () => {
    if (!regenStep) return;
    if (!regenFeedback.trim()) { toast.error('Descreva o ajuste desejado.'); return; }
    setActionStepId(regenStep.id);
    try {
      await api.regenerateEcommerceVideoStep(job.id, regenStep.id, regenFeedback.trim(), regenStep.isImage ? (regenRefUrl || undefined) : undefined);
      toast.success('Regerando com seu ajuste...');
      setRegenStep(null);
      setRegenFeedback('');
      setRegenRefUrl('');
      onChanged();
    } catch (e: any) { toast.error(e.message || 'Erro'); }
    finally { setActionStepId(null); }
  };

  const pendingRegen = job.output?._pendingRegen || null;
  const failures = job.output?.failures || [];
  const summary = job.output?.summary || null;
  const isRetryingStep = (stepId: string) => retryingStepId === stepId
    || (isRunning && (job.steps || []).some(s => s.id === stepId && (s.status === 'running' || s.title === job.currentStep)));
  const visibleFailures = failures.filter(f => !(job.steps || []).some(s => s.id === f.stepId && s.status === 'done'));
  const isReprocessing = !!retryingStepId || (isRunning && failures.length > 0);

  // Reprocessa uma etapa que falhou em definitivo. O crédito dela já foi
  // estornado quando o job finalizou, então esta nova tentativa é cobrada
  // (cena ou avatar; frame e merge não têm custo próprio).
  const retryStep = async (stepId: string) => {
    if (retrySubmitting.current || busy || isRunning) return;
    retrySubmitting.current = true;
    setRetryingStepId(stepId);
    setBusy(true);
    try {
      await api.retryEcommerceVideoStep(job.id, stepId);
      toast.success('Reprocessando a etapa...');
      await onChanged();
    } catch (e: any) {
      toast.error(e.message || 'Erro ao reprocessar');
    } finally { setBusy(false); setRetryingStepId(null); retrySubmitting.current = false; }
  };
  const [askRemerge, setAskRemerge] = useState(false);
  const resolveRegen = async (accept: boolean) => {
    setBusy(true);
    try {
      const res = await api.resolveEcommerceVideoRegen(job.id, accept);
      toast.success(accept ? 'Novo asset definido como oficial.' : 'Mantido o asset atual.');
      await onChanged();
      if (res.canRemerge) setAskRemerge(true);
    } catch (e: any) { toast.error(e.message || 'Erro ao aplicar ajuste'); }
    finally { setBusy(false); }
  };
  const doRemerge = async () => {
    setAskRemerge(false);
    setBusy(true);
    try {
      await api.remergeEcommerceVideo(job.id);
      toast.success('Refazendo o vídeo final (merge) com a nova versão...');
      onChanged();
    } catch (e: any) { toast.error(e.message || 'Erro ao refazer merge'); }
    finally { setBusy(false); }
  };

  // Liga/desliga os overlays do vídeo final. Quando o merge guardou o vídeo
  // pré-overlay, o backend só troca a URL (instantâneo); senão remonta de verdade
  // (ffmpeg, sem custo, mas leva minutos) — o toast diz qual dos dois aconteceu.
  const [overlayBusy, setOverlayBusy] = useState(false);
  const toggleOverlays = async (enabled: boolean) => {
    if (overlayBusy || isRunning) return;
    const needsRemerge = enabled ? !mergeResult?.overlayUrl : !mergeResult?.preOverlayUrl;
    if (needsRemerge && !window.confirm(
      enabled
        ? 'Para voltar com os overlays é preciso remontar o vídeo final do zero. Não custa créditos, mas leva alguns minutos. Remontar agora?'
        : 'Este vídeo não guardou a versão sem overlays, então é preciso remontá-lo do zero sem eles. Não custa créditos, mas leva alguns minutos. Remontar agora?'
    )) return;
    setOverlayBusy(true);
    try {
      const res = await api.setEcommerceVideoOverlays(job.id, enabled);
      if (res.mode === 'remerge') toast.success(enabled ? 'Remontando o vídeo final com overlays… isso leva alguns minutos.' : 'Remontando o vídeo final sem overlays… isso leva alguns minutos.');
      else if (res.mode === 'noop') toast('O vídeo final já está assim.');
      else toast.success(enabled ? 'Overlays restaurados.' : 'Overlays removidos.');
      await onChanged();
    } catch (e: any) { toast.error(e.message || 'Erro ao alterar os overlays'); }
    finally { setOverlayBusy(false); }
  };

  const doSave = async (stepId: string, productId?: string) => {
    setActionStepId(stepId);
    try { await api.saveEcommerceVideoToProduct(job.id, stepId, productId); toast.success('Vídeo salvo no produto.'); }
    catch (e: any) { toast.error(e.message || 'Erro ao salvar'); } finally { setActionStepId(null); }
  };
  // Mantem o pai quando ele e selecionavel: o video Shopee vai no pai (shopee_item_images),
  // e descarta-lo sumia com o destino em todo anuncio Shopee com variacao.
  const flattenTargets = (targets: any[]) => targets.flatMap((target: any) => {
    const children = Array.isArray(target.children) ? target.children : [];
    if (!children.length) return [target];
    return target.selectable !== false ? [target, ...children] : children;
  });
  const loadMarketplaceTargets = async (productId?: string) => {
    const id = productId || job.productId;
    if (!id) return [];
    setTargetsLoading(true);
    try {
      const res = await api.listEcommerceMarketplaceTargets(id);
      const targets = res.targets || [];
      setMktTargets(targets);
      const videoTargets = flattenTargets(targets).filter((t: any) => t.marketplace === 'shopee' && (t.media || []).includes('video') && t.selectable !== false);
      setSelectedVideoTargets(videoTargets.map((t: any) => t.id));
      return videoTargets;
    } catch (e: any) { toast.error(e.message || 'Erro ao carregar anuncios'); return []; }
    finally { setTargetsLoading(false); }
  };
  const toggleVideoTarget = (id: string) => setSelectedVideoTargets(prev => prev.includes(id) ? prev.filter(item => item !== id) : [...prev, id]);
  const doSend = async (stepId: string, productId?: string) => {
    setActionStepId(stepId);
    try {
      let marketplaceTargets = flattenTargets(mktTargets).filter((target: any) => selectedVideoTargets.includes(target.id));
      if (hasProduct && !marketplaceTargets.length) marketplaceTargets = await loadMarketplaceTargets(productId);
      const out = await api.sendEcommerceVideoToMarketplace(job.id, stepId, productId, marketplaceTargets);
      const ok = (out.results || []).filter((r: any) => r.success);
      const fail = (out.results || []).filter((r: any) => !r.success);
      if (ok.length) toast.success(`Enviado para ${ok.length} anúncio(s): ${ok.map((r: any) => r.platform).join(', ')}`);
      fail.forEach((r: any) => toast.error(`${r.platform}: ${r.error}`));
      if (!ok.length && !fail.length) toast('Nenhum anúncio vinculado.');
    } catch (e: any) { toast.error(e.message || 'Erro ao enviar'); } finally { setActionStepId(null); }
  };
  // Jobs com produto usam o produto do job; jobs manuais abrem modal de busca p/ escolher o anúncio.
  const handleSave = (stepId: string) => hasProduct ? doSave(stepId) : setModalAction({ type: 'save', stepId });
  const handleSend = (stepId: string) => hasProduct ? doSend(stepId) : setModalAction({ type: 'send', stepId });
  const onPickProduct = async (productId: string) => {
    const act = modalAction; setModalAction(null); setModalQuery('');
    if (!act) return;
    if (act.type === 'save') await doSave(act.stepId, productId);
    else await doSend(act.stepId, productId);
  };

  const seoFor = (stepId: string) => seo.find((s: any) => s.stepId === stepId);
  const download = async (url: string, filename?: string) => {
    try {
      const resp = await fetch(url); const blob = await resp.blob();
      const objUrl = URL.createObjectURL(blob);
      const a = document.createElement('a'); a.href = objUrl; a.download = filename || `asset-${Date.now()}`; document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(objUrl);
    } catch { window.open(url, '_blank'); }
  };
  const downloadAll = async () => {
    for (const r of results) {
      const src = r.url || r.imageUrl; if (!src) continue;
      await download(src, seoFor(r.stepId)?.nome_arquivo);
      await new Promise(res => setTimeout(res, 400));
    }
  };

  const renderAsset = (r: any) => {
    const src = r.url || r.imageUrl;
    return IS_VIDEO(r.assetKind, r.kind)
      ? <video src={src} controls className="aspect-[9/16] w-full bg-black object-contain" />
      : <img src={src} alt={r.title} className="aspect-[9/16] w-full object-cover" />;
  };

  const filteredProducts = products.filter(p => {
    const q = modalQuery.trim().toLowerCase(); if (!q) return true;
    return String(p.name || '').toLowerCase().includes(q) || String(p.sku || '').toLowerCase().includes(q);
  });

  const isRegeneratingJob = job.status === 'regenerating';
  const isAwaitingAdjust = job.status === 'awaiting_adjustment';
  const regenTitle = isRegeneratingJob ? job.currentStep : null;

  return (
    <div className="space-y-4">
      <div>
        <div className="mb-1 flex items-center justify-between text-xs">
          <span className="font-semibold text-slate-700">{STATUS_LABEL[job.status] || job.status}{job.currentStep ? ` — ${job.currentStep}` : ''}</span>
          {!isRegeneratingJob && !isAwaitingAdjust && <span className="text-slate-500">{job.progress}%</span>}
        </div>
        {isRegeneratingJob || isReprocessing ? (
          <div className="h-3 w-full overflow-hidden rounded-full bg-slate-200">
            <div className="h-full w-1/3 animate-pulse rounded-full bg-blue-500" />
          </div>
        ) : !isAwaitingAdjust && (
          <div className="h-3 w-full overflow-hidden rounded-full bg-slate-200">
            <div className={`h-full rounded-full transition-all ${job.status === 'failed' ? 'bg-rose-500' : 'bg-indigo-600'}`} style={{ width: `${job.progress}%` }} />
          </div>
        )}
        {/* Entrega parcial: o resumo estruturado é mais útil que o job.error corrido,
            então substitui a mensagem quando o backend detalhou o que faltou. */}
        {visibleFailures.length > 0 ? (
          <div className="mt-2 rounded-xl border border-amber-200 bg-amber-50 p-3">
            <p className="text-xs font-semibold text-amber-900">
              {isReprocessing ? 'Reprocessamento em andamento. Os itens prontos continuam disponíveis.' : summary
                ? `${summary.delivered} de ${summary.requested} item(ns) gerado(s) — ${summary.failed} não saiu(ram).`
                : `${failures.length} etapa(s) não foi(ram) concluída(s).`}
            </p>
            <ul className="mt-1.5 space-y-1.5">
              {visibleFailures.map(f => (
                <li key={f.stepId} className="flex items-start justify-between gap-3 text-xs text-amber-800">
                  <span>
                    <span className="font-medium">{f.title}</span>
                    {f.kind === 'seo' ? ' (SEO)' : ''}: {isRetryingStep(f.stepId) ? 'Reprocessando esta etapa…' : f.message}
                  </span>
                  {f.kind !== 'seo' && (
                    <button
                      onClick={() => retryStep(f.stepId)}
                      disabled={busy || isRunning || !!retryingStepId}
                      aria-busy={isRetryingStep(f.stepId)}
                      title="Gera esta etapa novamente. Como o crédito dela foi devolvido, uma nova cobrança é feita."
                      className="shrink-0 rounded-lg border border-amber-400 bg-white px-2 py-1 text-[11px] font-semibold text-amber-900 hover:bg-amber-100 disabled:opacity-50"
                    >
                      {isRetryingStep(f.stepId) ? 'Reprocessando…' : 'Tentar novamente'}
                    </button>
                  )}
                </li>
              ))}
            </ul>
            {!isReprocessing && <p className="mt-1.5 text-[11px] text-amber-700">
              As tentativas automáticas foram esgotadas e os créditos das etapas não entregues foram devolvidos —
              reprocessar cobra o crédito novamente.
            </p>}
          </div>
        ) : job.error ? (
          <p className="mt-2 text-xs text-rose-600">{job.error}</p>
        ) : null}
      </div>

      {/* Aprovação de ajuste: asset atual × novo gerado */}
      {job.status === 'awaiting_adjustment' && pendingRegen && (
        <div className="rounded-2xl border border-amber-200 bg-amber-50 p-4">
          <p className="text-sm font-semibold text-amber-800">Escolha qual {pendingRegen.assetKind === 'video' ? 'vídeo' : 'imagem'} fica como oficial</p>
          <p className="mt-0.5 text-xs text-amber-700">Ajuste pedido: "{pendingRegen.feedback}"</p>
          <div className="mt-3 grid grid-cols-2 gap-3">
            {[
              { label: 'Atual', url: pendingRegen.oldUrl, accept: false, btn: 'Manter atual', cls: 'border-slate-300 bg-white text-slate-700 hover:bg-slate-50' },
              { label: 'Novo', url: pendingRegen.newUrl, accept: true, btn: 'Usar o novo', cls: 'bg-indigo-600 text-white hover:bg-indigo-500' },
            ].map(opt => (
              <div key={opt.label} className={`rounded-xl border-2 bg-white p-2 ${opt.accept ? 'border-indigo-400' : 'border-slate-200'}`}>
                <p className="mb-1.5 text-center text-[10px] font-bold uppercase tracking-wider text-slate-500">{opt.label}</p>
                {pendingRegen.assetKind === 'video'
                  ? <video src={opt.url} controls className="aspect-[9/16] w-full rounded-lg bg-black object-contain" />
                  : <img src={opt.url} alt={opt.label} className="aspect-[9/16] w-full rounded-lg object-cover" />}
                <button onClick={() => resolveRegen(opt.accept)} disabled={busy}
                  className={`mt-2 w-full rounded-lg border py-2 text-xs font-bold disabled:opacity-50 ${opt.cls}`}>
                  {opt.btn}
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {job.status === 'awaiting_approval' && approvalResult && (
        <div className="rounded-2xl border border-amber-200 bg-amber-50 p-4">
          <p className="text-sm font-semibold text-amber-800">Aprove o primeiro asset para gerar o restante automaticamente</p>
          <div className="mt-3 max-w-xs overflow-hidden rounded-xl border border-amber-200">{renderAsset(approvalResult)}</div>
          {/* Regerar aqui também gera mídia nova e é cobrado. Aprovar não custa. */}
          {approvalCost.length > 0 && (
            <CreditCostNotice
              className="mt-3"
              items={approvalCost}
              note="Aprovar não custa nada. Regerar gera mídia nova, cobrada pelo uso real quando sair — se falhar, não cobra."
            />
          )}
          <div className="mt-3 flex gap-2">
            <button onClick={approve} disabled={busy} className="rounded-xl bg-emerald-600 px-4 py-2 text-sm font-semibold text-white disabled:opacity-50">Aprovar e continuar</button>
            <button onClick={regenerateFirst} disabled={busy} className="rounded-xl border border-amber-300 bg-white px-4 py-2 text-sm font-semibold text-amber-700 disabled:opacity-50">Regerar</button>
          </div>
        </div>
      )}

      {!isAwaitingAdjust && results.length > 0 && (
        <div className="flex items-center justify-between">
          <p className="text-sm font-semibold text-slate-700">Assets gerados</p>
          <button onClick={downloadAll} className="rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50">⬇ Baixar todas</button>
        </div>
      )}

      {!isAwaitingAdjust && results.length > 0 && (
        <div className="grid grid-cols-2 gap-3 md:grid-cols-3">
          {results.map(r => {
            const s = seoFor(r.stepId);
            const src = r.url || r.imageUrl;
            const isVideo = IS_VIDEO(r.assetKind, r.kind);
            const acting = actionStepId === r.stepId;
            // Regerando: chamada em voo, step 'running' (legado) ou job em regeneração deste step.
            const stepRunning = (job.steps || []).find(st => st.id === r.stepId)?.status === 'running';
            const regenerating = acting || stepRunning || (isRegeneratingJob && regenTitle === r.title);
            return (
              <div key={r.stepId} className={`overflow-hidden rounded-xl border bg-white shadow-sm ${selectedStepId === r.stepId ? 'border-indigo-500 ring-1 ring-indigo-300' : 'border-slate-200'}`}>
                <div className="relative">
                  {renderAsset(r)}
                  {regenerating && (
                    <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 bg-blue-600/75 backdrop-blur-sm">
                      <span className="h-7 w-7 animate-spin rounded-full border-2 border-white/40 border-t-white" />
                      <span className="text-sm font-bold text-white animate-pulse">Regerando…</span>
                    </div>
                  )}
                </div>
                <div className="space-y-1.5 p-2">
                  <p className="text-xs font-semibold text-slate-700">{r.title}</p>
                  {s?.titulo_seo && <p className="text-[11px] text-slate-500" title={s.titulo_seo}>{s.titulo_seo}</p>}
                  <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                    <button onClick={() => download(src!, s?.nome_arquivo)} className="text-[11px] font-semibold text-indigo-600 hover:underline">Baixar</button>
                    <button onClick={() => regenerateStep(r.stepId)} disabled={regenerating || isRunning} className={`text-[11px] font-semibold disabled:opacity-100 ${regenerating ? 'text-blue-600 animate-pulse' : 'text-slate-600 hover:underline disabled:opacity-40'}`}>{regenerating ? 'Regerando…' : 'Regerar'}</button>
                    {r.kind === 'merge' && (
                      <button
                        onClick={() => toggleOverlays(r.overlaysOn === false)}
                        disabled={overlayBusy || isRunning || regenerating}
                        title={r.overlaysOn === false
                          ? (r.overlayUrl ? 'Volta na hora para a versão com overlays' : 'Remonta o vídeo do zero (alguns minutos)')
                          : (r.preOverlayUrl ? 'Troca na hora para a versão sem overlays' : 'Remonta o vídeo do zero sem overlays (alguns minutos)')}
                        className="text-[11px] font-semibold text-fuchsia-600 hover:underline disabled:opacity-40">
                        {overlayBusy
                          ? 'Aplicando…'
                          : r.overlaysOn === false
                            ? (r.overlayUrl ? 'Restaurar overlays' : 'Remontar com overlays')
                            : (r.preOverlayUrl ? 'Remover overlays' : 'Remontar sem overlays')}
                      </button>
                    )}
                    {isVideo && <button onClick={() => handleSave(r.stepId)} disabled={acting} className="text-[11px] font-semibold text-emerald-600 hover:underline disabled:opacity-40">Salvar no produto</button>}
                  </div>
                  {isVideo && (
                    <label className="flex items-center gap-1.5 text-[11px] font-medium text-slate-600">
                      <input type="radio" name="mkt-select" checked={selectedStepId === r.stepId} onChange={() => setSelectedStepId(r.stepId)} />
                      Selecionar este vídeo
                    </label>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {!isAwaitingAdjust && videoResults.length > 0 && (
        <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
          <p className="text-sm font-semibold text-slate-800">Ações do vídeo selecionado</p>
          <p className="mt-0.5 text-[11px] text-slate-500">{selectedStepId ? `Selecionado: ${results.find(r => r.stepId === selectedStepId)?.title || 'vídeo'}` : 'Selecione um vídeo acima para habilitar as ações.'}</p>
          <div className="mt-3 flex flex-wrap items-center gap-2">
            <button
              onClick={() => selectedStepId && handleSend(selectedStepId)}
              disabled={!selectedStepId || actionStepId === selectedStepId}
              className="rounded-xl bg-indigo-600 px-4 py-2 text-xs font-semibold text-white disabled:opacity-50">
              {selectedStepId && actionStepId === selectedStepId ? 'Enviando...' : '🛒 Enviar para marketplace'}
            </button>
            <button
              onClick={() => selectedStepId && setPublishStepId(selectedStepId)}
              disabled={!selectedStepId}
              className="rounded-xl bg-fuchsia-600 px-4 py-2 text-xs font-semibold text-white disabled:opacity-50">
              📣 Enviar para redes sociais
            </button>
          </div>
          {hasProduct && (
            <div className="mt-4 border-t border-slate-200 pt-3">
              <div className="mb-2 flex items-center justify-between">
                <p className="text-xs font-semibold text-slate-700">Anuncios Shopee para receber video</p>
                <button onClick={() => loadMarketplaceTargets()} disabled={targetsLoading} className="text-[11px] font-semibold text-indigo-600 hover:underline disabled:opacity-40">{targetsLoading ? 'Carregando...' : 'Carregar destinos'}</button>
              </div>
              {mktTargets.length === 0 && !targetsLoading && <p className="rounded-lg border border-dashed border-slate-200 px-3 py-2 text-xs text-slate-500">Carregue a lista para selecionar o anuncio Shopee pai. Mercado Livre nao recebe video via API hoje.</p>}
              <div className="space-y-1">
                {flattenTargets(mktTargets).filter((target: any) => target.marketplace === 'shopee' && (target.media || []).includes('video')).map((target: any) => (
                  <label key={target.id} className="flex items-start gap-2 rounded-lg border border-slate-200 bg-white p-2 text-xs text-slate-700">
                    <input type="checkbox" checked={selectedVideoTargets.includes(target.id)} onChange={() => toggleVideoTarget(target.id)} />
                    <span><b>{target.platform || 'Shopee'}</b> {target.label || target.title || target.listingId}<span className="ml-1 text-slate-400">{target.sku ? `SKU ${target.sku}` : ''}</span><br /><span className="text-[11px] text-slate-500">Video e anexado no anuncio pai Shopee.</span></span>
                  </label>
                ))}
              </div>
            </div>
          )}
          <p className="mt-2 text-[10px] text-slate-400">
            <b>Marketplace:</b> {hasProduct ? 'envia para os anúncios vinculados ao produto' : 'no envio manual, escolha o produto/anúncio na busca'} — apenas Shopee via API (ML em breve). <b>Redes sociais:</b> abre o modal de postagem completo (mesmo das Produções), com bloqueios por plataforma e tipo.
          </p>
        </div>
      )}

      {modalAction && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={() => setModalAction(null)}>
          <div className="w-full max-w-md rounded-2xl bg-white p-4 shadow-xl" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between">
              <p className="text-sm font-bold text-slate-900">{modalAction.type === 'send' ? 'Escolha o produto/anúncio de destino' : 'Escolha o produto para salvar'}</p>
              <button onClick={() => setModalAction(null)} className="text-slate-400 hover:text-slate-700">✕</button>
            </div>
            <input value={modalQuery} onChange={e => setModalQuery(e.target.value)} placeholder="Buscar por nome ou SKU..." className="mt-3 w-full rounded-xl border border-slate-300 px-3 py-2 text-sm" autoFocus />
            <div className="mt-2 max-h-72 overflow-y-auto rounded-xl border border-slate-200">
              {filteredProducts.map(p => {
                const links = Array.isArray(p.marketplaceUrls) ? p.marketplaceUrls.filter((l: any) => l && l.platform && l.listingId) : [];
                const thumb = (p.imageUrls || [])[0];
                return (
                  <button key={p.id} onClick={() => onPickProduct(p.id)} className="flex w-full items-center gap-3 border-b border-slate-100 px-3 py-2 text-left last:border-b-0 hover:bg-slate-50">
                    {thumb ? <img src={thumb} alt={p.name} className="h-10 w-10 flex-shrink-0 rounded-lg border border-slate-200 object-cover" /> : <span className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg border border-dashed border-slate-300 text-[9px] text-slate-400">sem foto</span>}
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-sm text-slate-700">{p.name}</span>
                      <span className="block truncate text-[11px] text-slate-400">{p.sku ? `SKU: ${p.sku} · ` : ''}{links.length} anúncio(s) vinculado(s)</span>
                    </span>
                  </button>
                );
              })}
              {filteredProducts.length === 0 && <p className="px-3 py-4 text-center text-xs text-slate-400">Nenhum produto encontrado.</p>}
            </div>
          </div>
        </div>
      )}

      {/* Modal de postagem em redes sociais (mesmo das Produções) — asset sintético do vídeo selecionado */}
      {publishStepId && (() => {
        const r = results.find(x => x.stepId === publishStepId);
        const src = r?.url || r?.imageUrl;
        if (!r || !src) return null;
        const seoTitle = seoFor(publishStepId)?.titulo_seo;
        const videoAsset: any = {
          id: r.stepId,
          title: r.title || seoTitle || 'Vídeo',
          type: 'video',
          status: 'ready',
          url: src,
          previewUrl: src,
          caption: r.script || '',
        };
        return (
          <PublishModal
            asset={videoAsset}
            allAssets={[videoAsset]}
            captionSeo={{ caption: r.script || '', ...(seoTitle ? { titulo_seo: seoTitle } : {}) }}
            onClose={() => setPublishStepId(null)}
            onPublished={() => { setPublishStepId(null); toast.success('Postagem enviada!'); }}
          />
        );
      })()}

      {/* Modal de regerar asset (mesmo estilo do Produzir Fotos) */}
      {regenStep && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={() => actionStepId === null && setRegenStep(null)}>
          <div className="w-full max-w-md rounded-2xl bg-white p-5 shadow-2xl" onClick={e => e.stopPropagation()}>
            <h3 className="text-base font-bold text-slate-900">Regerar asset</h3>
            <p className="mt-1 text-xs text-slate-500">{regenStep.title}</p>
            <label className="mt-3 block text-xs font-semibold text-slate-500">O que precisa ser mudado?</label>
            <p className="text-[11px] text-slate-400">Para vídeo, a IA refaz o prompt de movimento com seu ajuste e regera o clipe; para imagem, edita a imagem gerada.</p>
            <textarea value={regenFeedback} onChange={e => setRegenFeedback(e.target.value)} rows={4} autoFocus
              placeholder="Ex.: movimento de câmera mais lento, aproximar do produto, deixar a cena mais clara..."
              className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2 text-sm" />

            {/* Imagem de referência opcional — só para etapas de imagem */}
            {regenStep.isImage && (
              <>
                <label className="mt-3 block text-xs font-semibold text-slate-500">Imagem de referência (opcional)</label>
                <p className="text-[11px] text-slate-400">Ex.: um fundo, material ou composição que você quer como guia do ajuste.</p>
                <div className="mt-1.5 flex items-center gap-2">
                  {regenRefUrl ? (
                    <div className="relative">
                      <img src={regenRefUrl} alt="referência" className="h-16 w-16 rounded-lg border border-slate-200 object-cover" />
                      <button onClick={() => setRegenRefUrl('')}
                        className="absolute -right-1 -top-1 flex h-5 w-5 items-center justify-center rounded-full bg-rose-500 text-[10px] text-white">✕</button>
                    </div>
                  ) : (
                    <label className="flex h-16 w-16 cursor-pointer flex-col items-center justify-center rounded-lg border-2 border-dashed border-slate-300 bg-slate-50 text-center text-[10px] text-slate-400 hover:border-indigo-400">
                      {regenRefUploading ? '...' : '+ foto'}
                      <input type="file" accept="image/*" className="hidden" onChange={e => e.target.files?.[0] && uploadRegenRef(e.target.files[0])} />
                    </label>
                  )}
                </div>
              </>
            )}

            {/* Regerar cena/avatar gera mídia nova e é cobrado como uma geração.
                Frame e merge não custam, então não mostram aviso. */}
            {regenCost.length > 0 && (
              <CreditCostNotice
                className="mt-3"
                items={regenCost}
                note="Regerar gera mídia nova, cobrada pelo uso real quando sair. Se falhar, não cobra."
              />
            )}

            <div className="mt-4 flex justify-end gap-2">
              <button onClick={() => setRegenStep(null)} disabled={actionStepId !== null} className="rounded-xl border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-700 disabled:opacity-50">Cancelar</button>
              <button onClick={submitRegen} disabled={actionStepId !== null || !regenFeedback.trim()} className="rounded-xl bg-amber-600 px-4 py-2 text-sm font-semibold text-white disabled:opacity-50">{actionStepId !== null ? 'Enviando...' : 'Regerar'}</button>
            </div>
          </div>
        </div>
      )}

      {/* Pergunta: refazer o merge (vídeo final) com a nova versão? */}
      {askRemerge && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={() => !busy && setAskRemerge(false)}>
          <div className="w-full max-w-md rounded-2xl bg-white p-5 shadow-2xl" onClick={e => e.stopPropagation()}>
            <h3 className="text-base font-bold text-slate-900">Refazer o vídeo final?</h3>
            <p className="mt-2 text-sm text-slate-600">Você atualizou um dos vídeos. Deseja refazer o merge (vídeo final montado) usando a nova versão? O merge antigo será substituído.</p>
            <div className="mt-4 flex justify-end gap-2">
              <button onClick={() => setAskRemerge(false)} disabled={busy} className="rounded-xl border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-700 disabled:opacity-50">Agora não</button>
              <button onClick={doRemerge} disabled={busy} className="rounded-xl bg-indigo-600 px-4 py-2 text-sm font-semibold text-white disabled:opacity-50">Refazer merge</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── View principal ──────────────────────────────────────────────────────────

// ─── View principal ──────────────────────────────────────────────────────────
export function EcommerceVideosView({ brand }: { brand: any }) {
  const [tab, setTab] = useState<'gerar' | 'estilos'>('gerar');
  const [pipelines, setPipelines] = useState<Pipeline[]>([]);
  const [products, setProducts] = useState<any[]>([]);
  const [avatarLooks, setAvatarLooks] = useState<{ id: string; name: string }[]>([]);
  const [selectedPipelineId, setSelectedPipelineId] = useState('');
  const [editPipelineId, setEditPipelineId] = useState('');
  const [editorDirty, setEditorDirty] = useState(false);
  const [selectedProductId, setSelectedProductId] = useState('');
  const [useBrand, setUseBrand] = useState(false);
  const [source, setSource] = useState<ProductSource>('system');
  const [manual, setManual] = useState<ManualProduct>(EMPTY_MANUAL);
  const [manualImageUrl, setManualImageUrl] = useState('');
  const [manualRefs, setManualRefs] = useState<string[]>([]);
  const [activeJob, setActiveJob] = useState<VideoJob | null>(null);
  const [jobs, setJobs] = useState<VideoJob[]>([]);
  const [loading, setLoading] = useState(true);
  const [starting, setStarting] = useState(false);
  // Passo a passo da geração: 1 Produto → 2 Estilo → 3 Revisar e gerar.
  const [step, setStep] = useState(1);
  const [maxStep, setMaxStep] = useState(1);
  const topRef = useRef<HTMLDivElement>(null);
  const { request: requestVideoConfirm, modal: videoConfirmModal } = useCreditConfirm();
  const pollRef = useRef<any>(null);

  const load = async () => {
    try {
      const [pRes, prodRes, jobRes, avRes] = await Promise.all([
        api.listEcommerceVideoPipelines(), api.listProducts(), api.listEcommerceVideoJobs(),
        api.listMarketingAvatars().catch(() => ({ avatars: [] })),
      ]);
      setPipelines(pRes.pipelines || []);
      setProducts(prodRes.products || []);
      setJobs(jobRes.jobs || []);
      // Looks JÁ EXISTENTES do avatar do tenant (prontos, não-chromakey) p/ usar como referência.
      const looks = (avRes.avatars || []).flatMap((a: any) => (a.looks || [])
        .filter((l: any) => l.status === 'ready' && !l.isChromakey)
        .map((l: any) => ({ id: l.id, name: l.name || 'Look' })));
      setAvatarLooks(looks);
      if (pRes.pipelines?.length) {
        setSelectedPipelineId(prev => prev || pRes.pipelines[0].id);
        setEditPipelineId(prev => prev || pRes.pipelines[0].id);
      }
    } catch (e: any) { toast.error(e.message || 'Erro ao carregar'); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  useEffect(() => {
    if (!activeJob) return;
    if (['completed', 'failed'].includes(activeJob.status)) return;
    pollRef.current = setInterval(async () => {
      try {
        const res = await api.getEcommerceVideoJob(activeJob.id);
        setActiveJob(res.job);
        if (['completed', 'failed'].includes(res.job.status)) clearInterval(pollRef.current);
      } catch {}
    }, 4000);
    return () => clearInterval(pollRef.current);
  }, [activeJob?.id, activeJob?.status]);

  const selectedPipeline = pipelines.find(p => p.id === selectedPipelineId);
  const pipelineSteps = selectedPipeline?.steps || [];
  const pipelineCostItems = usePipelineCostItems('video', pipelineSteps);
  const deliverables = pipelineSteps.filter(s => s.kind !== 'seo');
  const finalVideo = pipelineSteps.find(s => s.kind === 'merge');
  const selectedProduct = products.find(p => p.id === selectedProductId);
  const brandStepTitles = pipelineSteps.filter(s => s.useBrand).map(s => s.title);
  // Etapas que recebem "Referência #N" — é para elas que as fotos de referência servem.
  const refsUsedBy = selectedPipeline
    ? pipelineSteps.filter(s => (s.references || []).some(r => r.startsWith('ref:'))).map(s => s.title)
    : null;

  const missing: string[] = [];
  if (source === 'system' && !selectedProductId) missing.push('Escolher um produto');
  if (source === 'manual' && !manualImageUrl) missing.push('Enviar a foto do produto');
  if (source === 'manual' && !manual.name.trim()) missing.push('Informar o nome do produto');
  if (!selectedPipeline) missing.push('Escolher um estilo');
  else if (!pipelineSteps.length) missing.push('O estilo escolhido não tem etapas');

  const start = async () => {
    if (missing.length) { toast.error(missing[0]); return; }
    setStarting(true);
    try {
      const payload: any = { pipelineId: selectedPipelineId, brandId: brand?.id || null, useBrand };
      if (source === 'manual') payload.manual = { ...manual, imageUrl: manualImageUrl, referenceImages: manualRefs };
      else payload.productId = selectedProductId;
      const res = await api.startEcommerceVideoJob(payload);
      setActiveJob(res.job); toast.success('Geração iniciada'); load();
    } catch (e: any) { toast.error(e.message || 'Erro ao iniciar'); }
    finally { setStarting(false); }
  };

  const confirmLeaveEditor = () => !editorDirty || window.confirm('Há alterações não salvas neste estilo. Descartar?');
  const goTab = (t: 'gerar' | 'estilos') => {
    if (t === tab) return;
    if (tab === 'estilos' && !confirmLeaveEditor()) return;
    setEditorDirty(false);
    setTab(t);
  };
  const openEditor = (id: string) => {
    if (tab === 'estilos' && id !== editPipelineId && !confirmLeaveEditor()) return;
    setEditorDirty(false);
    setEditPipelineId(id);
    setTab('estilos');
  };
  const createPipeline = async () => {
    if (tab === 'estilos' && !confirmLeaveEditor()) return;
    try {
      const res = await api.createEcommerceVideoPipeline({ name: 'Meu estilo de vídeo' });
      toast.success('Estilo criado — adicione as etapas');
      await load();
      setEditorDirty(false);
      setEditPipelineId(res.pipeline.id);
      setTab('estilos');
    } catch (e: any) { toast.error(e.message || 'Erro ao criar'); }
  };

  const editPipeline = pipelines.find(p => p.id === editPipelineId);

  const openJob = async (id: string) => {
    try { const res = await api.getEcommerceVideoJob(id); setActiveJob(res.job); }
    catch (e: any) { toast.error(e.message || 'Erro'); }
  };
  const removeJob = async (id: string) => {
    if (!window.confirm('Remover esta geração?')) return;
    try { await api.deleteEcommerceVideoJob(id); if (activeJob?.id === id) setActiveJob(null); load(); }
    catch (e: any) { toast.error(e.message || 'Erro'); }
  };

  if (loading) return <div className="p-6 text-sm text-slate-500">Carregando...</div>;

  const productDone = source === 'system' ? !!selectedProductId : !!manualImageUrl && !!manual.name.trim();
  const productMissing = missing.filter(m => !m.toLowerCase().includes('estilo'));
  const styleDone = !!selectedPipeline && pipelineSteps.length > 0;
  // Só dá para pular para um passo cujos anteriores estão completos.
  const reached = Math.min(maxStep, productDone ? (styleDone ? 3 : 2) : 1);
  const goStep = (n: number) => {
    if (n > 1 && !productDone) return;
    if (n > 2 && !styleDone) return;
    setStep(n);
    setMaxStep(m => Math.max(m, n));
    topRef.current?.scrollIntoView({ block: 'start', behavior: 'smooth' });
  };
  const summaryThumb = source === 'system' ? (selectedProduct?.imageUrls || [])[0] : manualImageUrl;
  const summaryName = source === 'system' ? selectedProduct?.name : manual.name.trim();

  return (
    <div ref={topRef} className="space-y-5">
      {videoConfirmModal}
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 className="text-lg font-bold text-slate-900">Vídeos E-commerce</h2>
          <p className="text-sm text-slate-500">Escolha o produto e o estilo. A IA gera clipes, narração com avatar e monta o vídeo final.</p>
        </div>
        <div className="flex rounded-xl bg-slate-100 p-1">
          {([['gerar', 'Gerar vídeos'], ['estilos', 'Meus estilos']] as const).map(([t, label]) => (
            <button key={t} type="button" onClick={() => goTab(t)}
              className={`rounded-lg px-4 py-2 text-sm font-semibold transition ${tab === t ? 'bg-slate-950 text-white shadow-sm' : 'text-slate-600 hover:text-slate-950'}`}>
              {label}
            </button>
          ))}
        </div>
      </div>

      {tab === 'estilos' && (
        <div className="space-y-4">
          <div className="flex flex-wrap items-center gap-2">
            {pipelines.map(p => (
              <button key={p.id} type="button" onClick={() => openEditor(p.id)}
                className={`rounded-full border px-3 py-1.5 text-xs font-semibold transition ${p.id === editPipelineId ? 'border-slate-900 bg-slate-900 text-white' : 'border-slate-300 bg-white text-slate-700 hover:border-slate-400'}`}>
                {p.isDefault ? '🔒 ' : ''}{p.name}
              </button>
            ))}
            <button type="button" onClick={createPipeline} className="rounded-full border border-dashed border-slate-300 px-3 py-1.5 text-xs font-semibold text-slate-600 hover:border-indigo-400 hover:text-indigo-600">+ Novo estilo</button>
          </div>
          {editPipeline && (
            <VideoPipelineFlow
              pipeline={editPipeline}
              avatarLooks={avatarLooks}
              onDirtyChange={setEditorDirty}
              onSaved={(p) => setPipelines(list => list.map(x => x.id === p.id ? p : x))}
              onCloned={async (p) => { await load(); setEditorDirty(false); setEditPipelineId(p.id); }}
              onDeleted={async (id) => {
                const fallback = pipelines.find(p => p.id !== id)?.id || '';
                setEditorDirty(false);
                setEditPipelineId(fallback);
                if (selectedPipelineId === id) setSelectedPipelineId(fallback);
                await load();
              }}
            />
          )}
        </div>
      )}

      {tab === 'gerar' && (
        <>
          <WizardHeader steps={['Produto', 'Estilo do vídeo', 'Revisar e gerar']} current={step} reached={reached} onGo={goStep} />

          {step === 1 && (
            <div>
              <section className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm sm:p-5">
                <h3 className="text-base font-bold text-slate-900">De qual produto é o vídeo?</h3>
                <p className="mb-4 text-xs text-slate-500">Escolha do catálogo ou envie a foto de um produto que ainda não está cadastrado.</p>
                <ProductSourcePicker
                  source={source} onSource={setSource}
                  products={products} selectedProductId={selectedProductId} onSelectProduct={setSelectedProductId}
                  manual={manual} onManual={setManual}
                  manualImageUrl={manualImageUrl} onManualImage={setManualImageUrl}
                  manualRefs={manualRefs} onManualRefs={setManualRefs}
                  upload={(fd) => api.uploadEcommerceVideoReference(fd)}
                  refsUsedBy={null}
                  showAmbiente={false}
                  firstAssetLabel="primeira cena"
                />
              </section>
              <WizardFooter
                info={productDone && (
                  <div className="flex min-w-0 items-center gap-2">
                    {summaryThumb && <img src={summaryThumb} alt="" className="h-9 w-9 flex-shrink-0 rounded-lg border border-slate-200 object-cover" />}
                    <span className="truncate text-sm font-semibold text-slate-800">{summaryName}</span>
                  </div>
                )}
                onNext={() => goStep(2)} nextLabel="Continuar: escolher estilo →"
                nextDisabled={!productDone} nextHint={productMissing[0]} />
            </div>
          )}

          {step === 2 && (
            <div>
              <section className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm sm:p-5">
                <h3 className="text-base font-bold text-slate-900">Qual estilo de vídeo?</h3>
                <p className="mb-4 text-xs text-slate-500">Cada estilo é uma sequência de cenas, clipes, avatar e montagem final. Você pode criar o seu em "Meus estilos".</p>
                <PipelinePicker
                  pipelines={pipelines} selectedId={selectedPipelineId} onSelect={setSelectedPipelineId}
                  stepChip={videoStepChip} estimateKind="video"
                  onEdit={openEditor} onCreate={createPipeline}
                />
              </section>
              <WizardFooter
                onBack={() => goStep(1)}
                info={selectedPipeline && <span className="truncate text-sm font-semibold text-slate-800">{selectedPipeline.name} · {pipelineSteps.length} etapa{pipelineSteps.length === 1 ? '' : 's'}</span>}
                onNext={() => goStep(3)} nextLabel="Continuar: revisar →"
                nextDisabled={!styleDone} nextHint={!selectedPipeline ? 'Escolha um estilo' : 'O estilo escolhido não tem etapas'} />
            </div>
          )}

          {step === 3 && (
            <div className="grid gap-5 lg:grid-cols-[minmax(0,1fr)_360px]">
              <section className="space-y-4 rounded-2xl border border-slate-200 bg-white p-4 shadow-sm sm:p-5">
                <h3 className="text-base font-bold text-slate-900">Confira antes de gerar</h3>

                <div className="flex items-center gap-4 rounded-xl border border-slate-200 p-3">
                  {summaryThumb
                    ? <img src={summaryThumb} alt="" className="h-20 w-20 flex-shrink-0 rounded-lg border border-slate-200 object-cover" />
                    : <span className="flex h-20 w-20 flex-shrink-0 items-center justify-center rounded-lg border border-dashed border-slate-300 text-2xl text-slate-300">📦</span>}
                  <div className="min-w-0 flex-1">
                    <p className="text-[11px] font-bold uppercase tracking-wide text-slate-400">Produto</p>
                    <p className="truncate text-sm font-bold text-slate-900">{summaryName}</p>
                    <p className="text-xs text-slate-500">{source === 'system' ? (selectedProduct?.sku ? `SKU ${selectedProduct.sku}` : 'Do catálogo') : 'Envio manual'}</p>
                  </div>
                  <button type="button" onClick={() => goStep(1)} className="rounded-lg px-2 py-1 text-xs font-semibold text-indigo-600 hover:bg-indigo-50">Trocar</button>
                </div>

                <div className="rounded-xl border border-slate-200 p-3">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="text-[11px] font-bold uppercase tracking-wide text-slate-400">Estilo</p>
                      <p className="truncate text-sm font-bold text-slate-900">{selectedPipeline?.name}</p>
                    </div>
                    <button type="button" onClick={() => goStep(2)} className="rounded-lg px-2 py-1 text-xs font-semibold text-indigo-600 hover:bg-indigo-50">Trocar</button>
                  </div>
                  <p className="mt-3 text-xs font-semibold text-slate-600">{finalVideo ? 'Etapas até o vídeo final' : 'Você vai receber'}{pipelineSteps.some(s => s.kind === 'seo') ? ' (+ títulos SEO)' : ''}:</p>
                  <ol className="mt-2 grid gap-1.5 sm:grid-cols-2">
                    {deliverables.map((s, i) => (
                      <li key={s.id} className={`flex items-center gap-2 rounded-lg px-2 py-1.5 text-xs ${s.kind === 'merge' ? 'bg-violet-50 font-semibold text-violet-900' : 'bg-slate-50 text-slate-700'}`}>
                        <span className="w-5 flex-shrink-0 text-center">{VIDEO_KIND_META[s.kind]?.icon}</span>
                        <span className="truncate">{s.title}</span>
                        {i === 0 && <span className="ml-auto flex-shrink-0 text-[10px] font-semibold text-amber-600">você aprova</span>}
                      </li>
                    ))}
                  </ol>
                  {source === 'manual' && manualRefs.length > 0 && !(refsUsedBy || []).length && (
                    <p className="mt-2 text-[11px] text-amber-700">As {manualRefs.length} foto(s) de referência que você enviou não são usadas por este estilo.</p>
                  )}
                  {(refsUsedBy || []).length > 0 && <p className="mt-2 text-[11px] text-slate-500">Fotos de referência entram em: {(refsUsedBy || []).join(', ')}.</p>}
                </div>

                <button type="button" onClick={() => goStep(2)} className="rounded-xl border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50">← Voltar</button>
              </section>

              <aside className="lg:sticky lg:top-4 lg:self-start">
                <div className="space-y-4 rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
                  <label className={`flex items-start gap-2.5 rounded-xl border p-2.5 ${brand ? 'border-slate-200' : 'border-slate-100 opacity-60'}`}>
                    <input type="checkbox" checked={useBrand} onChange={e => setUseBrand(e.target.checked)} disabled={!brand} className="mt-0.5 h-4 w-4" />
                    <span>
                      <span className="block text-xs font-semibold text-slate-800">Aplicar minha marca em tudo{brand ? ` (${brand.name})` : ''}</span>
                      <span className="block text-[11px] text-slate-500">
                        {!brand ? 'Cadastre uma Brand para usar.'
                          : brandStepTitles.length && !useBrand ? `Já aplicada pelo estilo em: ${brandStepTitles.join(', ')}.`
                          : 'Logo, cores e fontes em todas as etapas.'}
                      </span>
                    </span>
                  </label>

                  <CreditCostNotice items={pipelineCostItems} note={ESTIMATE_NOTE} />

                  <button type="button" onClick={() => requestVideoConfirm({
                    title: 'Produzir vídeos e-commerce',
                    items: pipelineCostItems,
                    note: ESTIMATE_NOTE,
                    confirmLabel: 'Gerar vídeo',
                    onConfirm: start,
                  })} disabled={starting || missing.length > 0}
                    className="w-full rounded-xl bg-indigo-600 px-5 py-3 text-sm font-bold text-white shadow-sm transition hover:bg-indigo-500 disabled:cursor-not-allowed disabled:bg-slate-300">
                    {starting ? 'Iniciando...' : 'Gerar vídeo'}
                  </button>
                  <p className="text-[11px] text-slate-400">O primeiro resultado sai para você aprovar; o resto é gerado em seguida, na ordem do estilo.</p>
                </div>
              </aside>
            </div>
          )}

          <GenerationsGallery jobs={jobs} products={products} onOpen={openJob} onRemove={removeJob} unit="arquivos" />
        </>
      )}

      {/* Detalhe da geração em painel lateral */}
      {activeJob && (
        <div className="fixed inset-0 z-40 flex justify-end bg-black/40" onClick={() => setActiveJob(null)}>
          <div className="h-full w-full max-w-3xl overflow-y-auto bg-white p-5 shadow-2xl" onClick={e => e.stopPropagation()}>
            <div className="mb-4 flex items-center justify-between gap-3">
              <div className="min-w-0">
                <h3 className="truncate text-base font-bold text-slate-900">{activeJob.input?.product?.name || products.find(p => p.id === activeJob.productId)?.name || 'Geração de vídeos'}</h3>
                <p className="text-xs text-slate-500">{STATUS_LABEL[activeJob.status] || activeJob.status} · {activeJob.progress}%</p>
              </div>
              <button type="button" onClick={() => setActiveJob(null)} className="shrink-0 rounded-lg border border-slate-300 px-3 py-1.5 text-sm text-slate-600 hover:bg-slate-50">Fechar</button>
            </div>
            <JobRunner job={activeJob} products={products} onChanged={async () => { const res = await api.getEcommerceVideoJob(activeJob.id); setActiveJob(res.job); load(); }} />
          </div>
        </div>
      )}
    </div>
  );
}
